/**
 * Reads a CSS variable from the document root and returns its resolved value.
 */
function resolveCSSVar(varName, fallback) {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return raw || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Blue-family theme colors — all stay within the brand's blue/cyan/indigo palette
 * but are bright enough to show on the blue→cyan gradient badge background.
 */
function getThemeColors() {
  const isDark = document.documentElement.getAttribute("data-theme") === "dark";

  if (isDark) {
    // Dark mode: ultra-bright whites and electric blues pop on dark bg
    return [
      "#ffffff", // pure white
      resolveCSSVar("--c-accent-lt", "#90e0ef"), // light cyan
      "#ffffff", // white again — creates a shimmer effect
      resolveCSSVar("--c-brand-lt", "#748ffc"), // bright indigo
      "#ffffff", // back to white
    ];
  } else {
    // Light mode / gradient badge (blue→cyan):
    // Use bright white + very light blue tints — they flash like light on water
    return [
      "#ffffff", // pure white
      "#cce7ff", // very light sky blue
      "#ffffff", // white
      "#b0d4ff", // pale blue
      "#ffffff", // white
    ];
  }
}

export async function animateBadge(element, options = {}) {
  if (!element) return null;

  const {
    repeat = -1,
    repeatDelay = 4,
    triggerStart = "top 90%",
    splitType = "words,chars",
    colors = null,
    squashDuration = 0.4,
    bounceDuration = 0.7,
    resetDuration = 1.2,
    colorDuration = 0.3,
    returnDuration = 0.7,
    resetColorDuration = 1.2,
    stagger = 0.03,
    onComplete,
  } = options;

  const { gsap } = await import("gsap");
  const { ScrollTrigger } = await import("gsap/ScrollTrigger");
  gsap.registerPlugin(ScrollTrigger);

  let SplitText, chroma;

  try {
    ({ SplitText } = await import("gsap/SplitText"));
    gsap.registerPlugin(SplitText);
  } catch (e) {
    console.warn("SplitText plugin not available");
    return null;
  }

  try {
    chroma = (await import("chroma-js")).default;
  } catch (e) {
    console.warn("chroma-js not available");
    return null;
  }

  // Resolve colors at call-time so dark/light mode is respected
  const resolvedColors = colors ?? getThemeColors();
  const gradient = chroma.scale(resolvedColors).mode("lch");

  // Split text
  const split = new SplitText(element, { type: splitType });
  const chars = split.chars;
  gsap.set(chars, { opacity: 1 });

  // No need to remove badge background — white/light-blue colors
  // are visible on top of the blue→cyan gradient as a shimmer/flash effect

  const tl = gsap.timeline({
    repeat,
    repeatDelay,
    paused: true,
    onComplete,
  });

  // 1. Squash down
  tl.to(chars, {
    scaleY: 0.6,
    duration: squashDuration,
    ease: "power3.out",
    stagger,
    transformOrigin: "center bottom",
  });

  const t = squashDuration;

  // 2a. Bounce up
  tl.to(chars, { yPercent: -20, duration: bounceDuration, ease: "elastic.out(1, 0.5)", stagger }, t);

  // 2b. Restore scaleY
  tl.to(chars, { scaleY: 1, duration: resetDuration, ease: "elastic.out(2.5, 0.2)", stagger }, t);

  // 2c. Apply shimmer gradient colors — white/light-blue wave across chars
  tl.to(
    chars,
    {
      color: (i, _el, targets) => gradient(i / Math.max(targets.length - 1, 1)).hex(),
      textShadow: (i, _el, targets) => {
        // Add a glow to make the shimmer pop even more on the gradient bg
        const c = gradient(i / Math.max(targets.length - 1, 1)).hex();
        return `0 0 12px ${c}, 0 0 24px ${c}88`;
      },
      duration: colorDuration,
      ease: "power2.out",
      stagger,
    },
    t,
  );

  // 3. Return to position
  tl.to(chars, { yPercent: 0, duration: returnDuration, ease: "back.out(1.4)", stagger }, t + 0.1);

  // 4. Fade back to original white + remove glow
  tl.to(
    chars,
    {
      color: "#ffffff",
      textShadow: "none",
      duration: resetColorDuration,
      stagger: stagger * 1.2,
    },
    "-=0.8",
  );

  // ── ScrollTrigger ──
  const restoreChars = () => {
    gsap.set(chars, { clearProps: "color,textShadow" });
  };

  ScrollTrigger.create({
    trigger: element,
    start: triggerStart,
    onEnter: () => tl.play(),
    onLeave: () => {
      tl.pause();
      tl.progress(0);
      restoreChars();
    },
    onEnterBack: () => tl.play(0),
    onLeaveBack: () => {
      tl.pause();
      tl.progress(0);
      restoreChars();
    },
  });

  return tl;
}
